import Header from "@/components/layouts/Header";
import ReportHeader from "@/components/ReportHeader";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ReportButton } from "@/components/ui/report-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { ClientEvents, TrackerPosition } from "@/interfaces";
import {
  parseEngineDataLevel,
  type EngineDataLevel,
} from "@/lib/engine-data-level";
import { errorHandler, formatDateOnly, toggleVisibility } from "@/lib/utils";
import {
  trackReportsService,
  VehicleJourneyData,
  VehicleJourneyReportTotals,
} from "@/services/api/trackReports";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import TravelPositionsDialog from "../TravelReport/TravelPositionsDialog";
import VehicleJourneyReportTable from "./VehicleJourneyReportTable";

const formSchema = z.object({
  vehicle_id: z
    .number({
      message: "Veículo é obrigatório",
    })
    .min(0, "Veículo é obrigatório"),
  start_date: z
    .string({
      message: "Data inicial é obrigatória",
    })
    .date("Data inicial é obrigatória"),
  end_date: z
    .string({
      message: "Data final é obrigatória",
    })
    .date("Data final é obrigatória"),
});

type FormValues = z.infer<typeof formSchema>;

const VehicleJourneyReport = () => {
  const [selectedJourneyData, setSelectedJourneyData] =
    useState<VehicleJourneyData>();
  const [selectedJourneyPositions, setSelectedJourneyPositions] = useState<
    TrackerPosition[]
  >([]);
  const [isFormVisible, setIsFormVisible] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isLoadingDialog, setIsLoadingDialog] = useState(false);
  const [driversWithColors, setDriversWithColors] = useState<
    Array<{
      name: string;
      color: string;
    }>
  >([]);
  const [events, setEvents] = useState<ClientEvents[]>([]);
  // O nível é propriedade do veículo e vem na resposta do relatório — não
  // depende de o seletor ter disparado callback nenhum.
  const [level, setLevel] = useState<EngineDataLevel>("ignition");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState<{
    vehicle_title: string;
    totals: VehicleJourneyReportTotals;
  }>({
    vehicle_title: "",
    totals: {
      over_speed_count: 0,
      timings: {
        movement: 0,
        stopped_with_ignition_on: 0,
        off: 0,
        over_speed: 0,
        rpm: {
          green: 0,
          yellow: 0,
          red: 0,
          idle: 0,
        },
        engine_on: 0,
        engine_idle: 0,
      },
      distance: 0,
      out_work_distance: 0,
      max_speed: 0,
      stops_count: 0,
      consumption: 0,
    },
  });

  const [vehicleJourneys, setVehicleJourneys] = useState<VehicleJourneyData[]>(
    [],
  );

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: undefined,
      start_date: format(firstDayOfMonth, "yyyy-MM-dd"),
      end_date: format(today, "yyyy-MM-dd"),
    },
  });

  const onSubmit = async (data: FormValues) => {
    setStartDate(data.start_date);
    setEndDate(data.end_date);
    try {
      const response = await trackReportsService.getVehicleJourneyReport(data);
      let journeys: VehicleJourneyData[] = [];

      if (response.data && typeof response.data === "object") {
        Object.keys(response.data).forEach((key) => {
          if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
            journeys = [
              ...journeys,
              { ...(response.data[key] as VehicleJourneyData), date: key },
            ];
          }
        });
      }

      setVehicleJourneys(journeys);
      setLevel(
        parseEngineDataLevel(
          response.data.engine_data_level,
          Boolean(response.data.has_engine_on_info),
        ),
      );
      setReportData({
        totals: {
          ...response.data.totals,
          timings: {
            ...response.data.totals.timings,
            rpm: response.data.totals.timings.rpm || {
              green: 0,
              yellow: 0,
              red: 0,
              idle: 0,
            },
          },
        },
        vehicle_title: `${response.data.vehicle_plate} ${response.data.vehicle_title}`,
      });
      setIsFormVisible(false);
    } catch (error) {
      // setReportData();
      console.log("🚀 ~ onSubmit ~ error:", error);
      errorHandler(error, "Nenhuma jornada encontrada.");
    }
  };

  const handleToggleFormVisibility = () => {
    toggleVisibility(isFormVisible, setIsFormVisible);
  };

  const handleMapButtonClick = async (journey: VehicleJourneyData) => {
    setSelectedJourneyData(journey);
    setDialogOpen(true);
    setIsLoadingDialog(true);
    setDriversWithColors([]);
    setEvents([]);

    try {
      if (journey.date) {
        const response = await trackReportsService.getMapReport({
          vehicle_id: form.getValues("vehicle_id"),
          start_date: journey.date,
          end_date: journey.date,
        });
        let positions: TrackerPosition[] = [];

        if (response.data && typeof response.data === "object") {
          Object.keys(response.data).forEach((key) => {
            // Check if the key matches the date format yyyy-mm-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
              positions = [...positions, ...response.data[key].positions];

              if (
                response.data[key].drivers_with_colors &&
                Array.isArray(response.data[key].drivers_with_colors)
              ) {
                setDriversWithColors(response.data[key].drivers_with_colors);
              }
            }
          });
          setSelectedJourneyPositions(positions);
        }

        if (response.data.events_data) {
          let eventsData: ClientEvents[] = [];
          Object.keys(response.data.events_data).forEach((key) => {
            eventsData = [...eventsData, ...response.data.events_data[key]];
          });
          setEvents(eventsData);
        }
      }
    } catch (error) {
      errorHandler(error, "Erro ao carregar posições do veículo.");
    } finally {
      setIsLoadingDialog(false);
    }
  };

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {
        vehicle_id: form.getValues("vehicle_id"),
        start_date: startDate,
        end_date: endDate,
      };

      const response =
        await trackReportsService.getVehicleJourneyReportPdf(params);

      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    } catch (error) {
      errorHandler(error, "Erro ao gerar PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleRowExpand = (journeyId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [journeyId]: !prev[journeyId],
    }));
  };

  return (
    <div className="h-full">
      <Header
        title="Jornada do veículo"
        showToggleButton
        isFormVisible={isFormVisible}
        onToggleFormVisibility={handleToggleFormVisibility}
      />

      <div
        className={`max-w-md transition-all duration-300 ease-in-out ${
          isFormVisible ? "opacity-100" : "opacity-0 max-h-0 overflow-hidden"
        }`}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="vehicle_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Veículo</FormLabel>
                  <VehicleSelect
                    value={field.value}
                    onChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <DateField form={form} name="start_date" label="De" />

            <DateField form={form} name="end_date" label="Até" />

            <ReportButton loading={form.formState.isSubmitting} />
          </form>
        </Form>
      </div>

      {!isFormVisible && (
        <div
          className={`w-full flex flex-col gap-4 transition-all duration-300 ease-in-out ${
            isFormVisible ? "opacity-0 max-h-0 overflow-hidden" : "opacity-100"
          }`}
        >
          {/* Cabecalho do relatorio */}
          <ReportHeader
            handleExportPdf={handleExportPdf}
            primaryInfo={reportData?.vehicle_title ?? ""}
            secondaryInfo={`${startDate?.split("-").reverse().join("/")} • ${endDate?.split("-").reverse().join("/")}`}
            loading={pdfLoading}
          />

          {/* Tabela com dados do relatorio */}
          <VehicleJourneyReportTable
            vehicleJourneys={vehicleJourneys}
            handleMapButtonClick={handleMapButtonClick}
            expandedRows={expandedRows}
            toggleRowExpand={toggleRowExpand}
            totals={reportData.totals}
            level={level}
          />
        </div>
      )}

      <TravelPositionsDialog
        period={
          selectedJourneyData?.date
            ? formatDateOnly(selectedJourneyData.date)
            : ""
        }
        vehicleTitle={reportData.vehicle_title}
        positions={selectedJourneyPositions}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        isLoading={isLoadingDialog}
        driversWithColors={driversWithColors}
        events={events}
      />
    </div>
  );
};

export default VehicleJourneyReport;
